import React, { type ReactElement, type ReactNode } from "react";
import { type OptionProps, type Option, SupSelect, SupFieldControl, SupOption } from "@repo/core-ui";

import { FieldProps } from "../types";
import { useField } from "../hooks";

export interface SelectFieldProps<V> extends Omit<FieldProps, "defaultValue"> {
  defaultOpen?: boolean;
  defaultValue?: V;
  children: ReactElement<OptionProps<V>, typeof SupOption>[];
  optionClassName?: string;
  popoverClassName?: string;
  popoverContentClassName?: string;
  renderSelectedOption?: (option: Option<V>) => ReactNode;
  renderOptionLabel?: (option: Option<V>, active: boolean) => ReactNode;
  renderOptionIcon?: (option: Option<V>, active: boolean) => ReactNode;
}

export function SupSelectField<V>({
  name,
  id = name,
  required,
  label,
  hint,
  hintAlign,
  prepend,
  className,
  controlClassName,
  fluid,
  disabled,
  autoFocus,
  color,
  defaultValue,
  placeholder,
  defaultOpen,
  children,
  optionClassName,
  popoverClassName,
  popoverContentClassName,
  shouldUnregister,
  icon,
  renderSelectedOption,
  renderOptionLabel,
  renderOptionIcon,
}: SelectFieldProps<V>) {
  const { ref, value = "" as V, error, invalid, onChange, onBlur } = useField({ name, defaultValue, autoFocus, shouldUnregister });
  const fieldColor = invalid ? "error" : color;

  return (
    <SupFieldControl
      disabled={disabled}
      fluid={fluid}
      required={required}
      id={id}
      label={label}
      hint={invalid ? error : hint}
      hintAlign={hintAlign}
      prepend={prepend}
      className={controlClassName}
      color={fieldColor}
      icon={icon}
    >
      <SupSelect
        ref={ref}
        id={id}
        name={name}
        defaultOpen={defaultOpen}
        className={className}
        color={fieldColor}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        optionClassName={optionClassName}
        popoverClassName={popoverClassName}
        popoverContentClassName={popoverContentClassName}
        renderSelectedOption={renderSelectedOption}
        renderOptionLabel={renderOptionLabel}
        renderOptionIcon={renderOptionIcon}
        onSelect={onChange}
        onBlur={onBlur}
      >
        {children}
      </SupSelect>
    </SupFieldControl>
  );
}
